// Adopt an existing `cloudgrid.yaml` — mint a server-side entity from it and
// write `.cloudgrid/link.json`, WITHOUT overwriting the yaml.
//
// CLOUDGRID.md §3 advertises that `grid init` / `grid plug` in an unlinked
// directory that already has a `cloudgrid.yaml` set the folder up from that
// yaml. The folder-rooted `grid init` (and `grid init --here`) historically
// REFUSED when a yaml was present, contradicting the doc. This helper is the
// adoption path: it reads the yaml's `name:` for the slug, registers the entity
// (the same `POST /api/v2/entities` call `init` makes), and links the folder —
// leaving the user's reviewed yaml exactly as it is.
//
// Identity stays server-first (Decision 012): the yaml carries shape only; the
// entity id + org binding come from the server, recorded in link.json.

import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import { parse as parseYaml } from 'yaml';

import { CloudGridError } from '../utils.js';
import type { IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { getActiveOrg, notSignedInMessage } from '../active-org.js';
import { appendGitignoreLine, readLink, writeLink, type LinkArtifact } from './link.js';
import { isValidSlug, suggestSlug } from '../commands/slug.js';

/** Shape of the POST /api/v2/entities response we read. Mirrors init.ts. */
interface EntityResponse {
  entity_id: string;
  name: string;
  kind: 'app' | 'agent';
  org_id: string;
}

export interface AdoptYamlOpts {
  /** `--grid` / `--org` — one-shot active-grid override for the create. */
  grid?: string;
}

export interface AdoptYamlDeps {
  /** CLI version stamped into link.json. */
  cliVersion: string;
  /** Output sink. Defaults to stdout. */
  writer?: (s: string) => void;
  /**
   * What the caller does next, so the closing line reads coherently:
   *   - `'plug'` — plug is about to deploy → "Charged. Plugging now..."
   *   - `'dev'`  — dev is about to tunnel  → "Charged. Starting dev..."
   *   - omitted  — standalone adopt → the "grid plug" next-step block.
   * This is the mint-at-plug seam (Decision 012, amended 2026-07-18): the
   * first substrate-touching verb adopts an unlinked yaml, and the message
   * names that verb rather than pointing back at a pre-mint state.
   */
  onward?: 'plug' | 'dev';
}

export type AdoptYamlResult = { kind: 'linked'; link: LinkArtifact };

/**
 * Read the top-level `name:` from a `cloudgrid.yaml`. Throws a §23-voice
 * CloudGridError when the file is unparseable or carries no usable name — the
 * yaml is the sole source of the slug on this path, so a missing name is a hard
 * stop rather than a silent guess.
 */
export function readYamlSlug(cwd: string): string {
  const yamlPath = join(cwd, 'cloudgrid.yaml');
  let parsed: unknown;
  try {
    parsed = parseYaml(readFileSync(yamlPath, 'utf-8'));
  } catch (err) {
    throw new CloudGridError(
      `cloudgrid.yaml is not valid YAML: ${(err as Error).message}`,
    );
  }
  const rawName =
    parsed && typeof parsed === 'object'
      ? (parsed as { name?: unknown }).name
      : undefined;
  if (typeof rawName !== 'string' || rawName.trim() === '') {
    throw new CloudGridError(
      [
        'cloudgrid.yaml has no name to adopt.',
        'Add a `name:` to the yaml, or run grid new to set it up explicitly.',
      ].join('\n'),
    );
  }
  const name = rawName.trim();
  if (!isValidSlug(name)) {
    const suggestion = suggestSlug(name);
    const lines = [
      `cloudgrid.yaml name '${name}' is not a valid slug (lowercase alphanumeric + hyphens).`,
    ];
    if (suggestion) lines.push(`Try renaming it to: ${suggestion}`);
    throw new CloudGridError(lines.join('\n'));
  }
  return name;
}

/**
 * Adopt the `cloudgrid.yaml` in `cwd`: mint an entity from its `name:` and write
 * `.cloudgrid/link.json`. NEVER overwrites the yaml. Guards against clobbering a
 * valid existing link — an already-linked directory is left untouched.
 *
 * Throws CloudGridError when:
 *   - no `cloudgrid.yaml` is present (caller routed us here by mistake),
 *   - the directory already carries a valid link (nothing to adopt),
 *   - the yaml has no usable `name:`,
 *   - entity creation fails server-side.
 */
export async function adoptExistingYaml(
  baseClient: IV2Client,
  cwd: string,
  opts: AdoptYamlOpts,
  deps: AdoptYamlDeps,
): Promise<AdoptYamlResult> {
  const writer = deps.writer ?? ((s: string) => process.stdout.write(s));

  if (!existsSync(join(cwd, 'cloudgrid.yaml'))) {
    throw new CloudGridError('No cloudgrid.yaml here to adopt.');
  }

  // Guard against overwriting a valid existing link. `readLink` throws
  // MalformedLinkError on a corrupt file — let that surface rather than silently
  // minting a second entity over a broken-but-present link.
  const existingLink = readLink(cwd);
  if (existingLink) {
    throw new CloudGridError(
      [
        'This folder is already linked to a CloudGrid entity.',
        'Run grid plug to deploy it.',
      ].join('\n'),
    );
  }

  const slug = readYamlSlug(cwd);

  // Resolve the active grid server-side, then bind the client to it so the
  // entity is created in the right hub. Mirrors init.ts / plug --existing.
  let active: { org_id: string; slug: string };
  try {
    active = await getActiveOrg(baseClient, { override: opts.grid ?? null });
  } catch (err) {
    if (err instanceof V2ApiError) throw mapEntityError(err);
    throw err;
  }
  const client = baseClient.withOrgSlug(active.slug);

  let entity: EntityResponse;
  try {
    entity = await client.post<EntityResponse>('/api/v2/entities', {
      kind: 'app',
      name: slug,
    });
  } catch (err) {
    if (err instanceof V2ApiError) throw mapEntityError(err);
    throw err;
  }

  // The server may have suffixed the slug on collision; surface it once. The
  // yaml `name:` stays as the user wrote it (display-only post-link per §3); the
  // link's `entity_slug` carries the disambiguated server slug.
  if (entity.name !== slug) {
    writer(
      `Note: '${slug}' was taken in ${active.slug}-hub; using '${entity.name}'.\n`,
    );
  }

  const link: LinkArtifact = {
    entity_id: entity.entity_id,
    org_id: entity.org_id ?? active.org_id,
    org_slug: active.slug,
    kind: entity.kind === 'agent' ? 'agent' : 'app',
    entity_slug: entity.name,
    created_at: new Date().toISOString(),
    cli_version: deps.cliVersion,
  };
  writeLink(link, cwd);
  appendGitignoreLine('.cloudgrid/', cwd);
  appendGitignoreLine('.cloudgrid-dev.lock', cwd);
  appendGitignoreLine('.env.local', cwd);

  const head = [
    `Adopting ${entity.name} (app) in ${active.slug}-hub from the existing cloudgrid.yaml...`,
    '  Kept cloudgrid.yaml (unchanged)',
    '  Linked .cloudgrid/link.json',
  ];
  if (deps.onward === 'plug') {
    writer([...head, '  Charged. Plugging now...', ''].join('\n') + '\n');
  } else if (deps.onward === 'dev') {
    writer([...head, '  Charged. Starting dev...', ''].join('\n') + '\n');
  } else {
    writer(
      [
        ...head,
        '  Charged. Not yet live.',
        '',
        '  grid plug    # register on the grid and deploy',
        '',
      ].join('\n') + '\n',
    );
  }

  return { kind: 'linked', link };
}

/** Map POST /api/v2/entities (and active-grid) errors to §6.5-style messages. */
function mapEntityError(err: V2ApiError): CloudGridError {
  if (
    err.code === 'AUTH_REQUIRED' ||
    err.code === 'AUTH_EXPIRED' ||
    err.code === 'AUTH_INVALID'
  ) {
    return new CloudGridError(notSignedInMessage());
  }
  if (err.code === 'FORBIDDEN_ROLE') {
    return new CloudGridError(
      'Creating an entity requires admin or builder role on this grid.',
    );
  }
  if (err.code === 'CONFLICT') {
    return new CloudGridError(err.message);
  }
  if (err.code === 'VALIDATION_FAILED') {
    return new CloudGridError(err.message);
  }
  return new CloudGridError(
    `Server error (${err.status} ${err.code}). Try again, or run grid feedback if it persists.`,
  );
}
